/**
 * Configuration for React Query context auto-registration
 */

/**
 * Query keys to ignore from auto-registration.
 * Uses string.includes() matching, so partial matches will be excluded.
 *
 * Note: These are the actual query keys, not the context IDs shown in the debug window.
 * The debug window adds a 'query-' prefix, so 'build' becomes 'query-build'.
 *
 * Examples:
 * - 'build' will exclude the 'build' query (shown as 'query-build' in debug window)
 * - 'region' will exclude the 'region' query (shown as 'query-region' in debug window)
 */
export const IGNORED_QUERY_KEYS = [
  'build', // Excludes get-build-number query
  'region', // Excludes region queries
  // Add more query keys to ignore here as needed
] as const;

/**
 * Global configuration for React Query context auto-registration.
 */
export const REACT_QUERY_CONTEXT_CONFIG = {
  /** Maximum size for auto-registered contexts (50KB default) */
  MAX_CONTEXT_SIZE: 50 * 1024, // 50KB in bytes
  /** Maximum stub size before we skip it entirely (20KB) */
  MAX_STUB_SIZE: 20 * 1024, // 20KB in bytes
  /** Number of sample records to include in stub */
  STUB_SAMPLE_SIZE: 1, // Reduced to 1 to keep stubs small
  /** Whether to log when contexts are skipped due to size */
  LOG_SKIPPED: true,
};

/**
 * Cache for storing full data of large queries.
 * Key: query ID (e.g., "query-inventoryPackages")
 * Value: full query data (stored as unknown for type safety)
 *
 * Note: We use `unknown` here because the cache can store any type of query data.
 * Consumers should validate/cast the data appropriately when retrieving.
 */
const largeQueryDataCache = new Map<string, unknown>();

/**
 * Store full data for a large query.
 *
 * @param queryId - Unique identifier for the query
 * @param data - Query data to cache (accepts any type via unknown)
 */
export function storeLargeQueryData(queryId: string, data: unknown): void {
  largeQueryDataCache.set(queryId, data);
}

/**
 * Retrieve full data for a large query.
 *
 * @param queryId - Unique identifier for the query
 * @returns The cached query data, or undefined if not found
 *
 * Note: Returns `unknown` - consumers should validate the data structure
 * before using it, as we cannot guarantee type safety for cached data.
 */
export function getLargeQueryData(queryId: string): unknown {
  return largeQueryDataCache.get(queryId);
}

/**
 * Clear large query data cache.
 */
export function clearLargeQueryCache(): void {
  largeQueryDataCache.clear();
}
